<script setup lang="ts">
import {
  type EditorFileBrowserEntry,
  type EditorFileBrowserSelection,
} from "#bb/lib/editorFileBrowser";
import { toFileApiPath } from "#bb/lib/pathContract";
import {
  isApplicationFileVideoSource,
  resolveSelectedFileVideoSource,
} from "#bb/lib/video";
import type { VideoSource } from "#bb/types/blocks/content/VideoOption";

const props = defineProps<{ id: string }>();
const { siteId } = useSite();
const logger = useLogger("VideoEditor");
const { field } = useInlineBlockEditor(props.id);
const resolvingSource = shallowRef(false);

const source = field<VideoSource | null>("source", null);
const title = field<string>("title", "Video");
const autoplay = field<boolean>("autoplay", false);
const controls = field<boolean>("controls", true);
const loop = field<boolean>("loop", false);
const muted = field<boolean>("muted", false);
const startSeconds = field<number>("startSeconds", 0);
const width = field<number>("width", 0);
const height = field<number>("height", 0);

const selectedPath = computed({
  get: () => {
    if (isApplicationFileVideoSource(source.value)) return source.value.path;
    return source.value?.provider === "cloudflare-stream"
      ? source.value.path ?? ""
      : "";
  },
  set: (path: string) => {
    if (!path) {
      source.value = null;
      return;
    }
    source.value = {
      provider: "application-file",
      fileId: path,
      path,
    };
  },
});

const providerLabel = computed(() => {
  if (source.value?.provider === "cloudflare-stream") return "Cloudflare Stream";
  if (source.value?.provider === "jwplayer") return "JW Player";
  if (source.value?.provider === "application-file") return "Application file";
  return "No video selected";
});

async function selectVideo(
  path: string,
  selection: EditorFileBrowserSelection,
) {
  if (!selection.entry) return;

  source.value = resolveSelectedFileVideoSource(selection.entry, path);
  resolvingSource.value = true;
  try {
    const entry = await useApiFetch<EditorFileBrowserEntry>(
      `/v1/file/${toFileApiPath(selection.entry.path)}`,
      {
        headers: {
          "x-site-id": siteId.value,
          "x-include-domain-contracts": "metadata",
        },
      },
    );
    source.value = resolveSelectedFileVideoSource(entry, path);
  } catch (error) {
    logger.warn("Unable to refresh selected video metadata", {
      path: selection.entry.path,
      error,
    });
  } finally {
    resolvingSource.value = false;
  }
}
</script>

<template>
  <EditorSection title="Video" icon="lucide:video" accent="media">
    <EditorField
      label="Video file"
      hint="Choose an uploaded video. Cloudflare Stream playback is used when processing metadata is available."
    >
      <EditorPathInput
        v-model="selectedPath"
        mode="existing-file"
        :scopes="['site']"
        default-scope="site"
        :include-content-types="['video/*']"
        content-type-filter="all"
        browse-title="Choose a video"
        browse-description="Select an uploaded site video."
        placeholder="/media/video.mp4"
        aria-label="Video file"
        @select="selectVideo"
      />
    </EditorField>

    <div class="flex items-center gap-2 rounded-sm border bg-muted/35 px-2.5 py-2 text-[11.5px] text-muted-foreground">
      <Icon name="lucide:circle-play" class="size-3.5 text-[color:var(--cat-media)]" />
      <span>{{ resolvingSource ? "Checking video provider..." : providerLabel }}</span>
    </div>

    <EditorField label="Accessible title" hint="Describes the player for screen-reader users.">
      <Input
        v-model="title"
        class="h-8 rounded-sm bg-muted text-[13px]"
        placeholder="Introduction video"
      />
    </EditorField>
  </EditorSection>

  <EditorSection
    title="Playback"
    icon="lucide:sliders-horizontal"
    accent="media"
    :default-open="false"
  >
    <EditorField label="Autoplay">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">Start automatically and muted.</span>
        <Switch v-model="autoplay" />
      </div>
    </EditorField>
    <EditorField label="Player controls">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">Allow playback and seeking controls.</span>
        <Switch v-model="controls" />
      </div>
    </EditorField>
    <EditorField label="Loop video">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">Restart after playback completes.</span>
        <Switch v-model="loop" />
      </div>
    </EditorField>
    <EditorField label="Start muted">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">Begin playback without sound.</span>
        <Switch v-model="muted" />
      </div>
    </EditorField>
    <EditorField label="Start time (seconds)">
      <Input
        v-model.number="startSeconds"
        type="number"
        min="0"
        step="1"
        class="h-8 rounded-sm bg-muted text-[13px]"
      />
    </EditorField>
  </EditorSection>

  <EditorSection
    title="Dimensions"
    icon="lucide:move-diagonal-2"
    accent="media"
    :default-open="false"
  >
    <EditorField label="Width (pixels)" hint="Set to 0 to fill the available width.">
      <Input
        v-model.number="width"
        type="number"
        min="0"
        step="1"
        class="h-8 rounded-sm bg-muted text-[13px]"
      />
    </EditorField>
    <EditorField label="Height (pixels)" hint="Set to 0 to use a 16:9 aspect ratio.">
      <Input
        v-model.number="height"
        type="number"
        min="0"
        step="1"
        class="h-8 rounded-sm bg-muted text-[13px]"
      />
    </EditorField>
  </EditorSection>
</template>
